/** ContextSwitcher — pill dropdown in AppHeader letting the user
 *  flip between "You" (personal profile) and any PH Page they
 *  administer. Facebook / LinkedIn style Page toggle.
 */
import { useState, useEffect, useRef } from "react";
import { ChevronDown, User, Briefcase, Check } from "lucide-react";
import { useMailbox } from "@/contexts/MailboxContext";

const ICONS = { user: User, briefcase: Briefcase };

export default function ContextSwitcher() {
  const { contexts, activeContext, setActive } = useMailbox();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (!contexts || contexts.length <= 1) return null;   // no PH pages → hide

  const ActiveIcon = ICONS[activeContext.icon] || User;

  return (
    <div className="relative" ref={rootRef} data-testid="context-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-9 px-3 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] text-white transition"
        data-testid="context-switcher-trigger"
        title="Switch identity — personal vs page"
      >
        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
          <ActiveIcon size={13} className="text-white/85" />
        </div>
        <div className="leading-tight text-left hidden md:block">
          <div className="text-[9px] tracking-[0.2em] uppercase text-white/45">
            {activeContext.kind === "personal" ? "Personal" : "Page"}
          </div>
          <div className="text-[12px] font-display font-semibold truncate max-w-[140px]">
            {activeContext.title}
          </div>
        </div>
        <ChevronDown size={13} className="text-white/55" />
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-2 min-w-[260px] rounded-xl border border-white/15 bg-neutral-950/95 backdrop-blur-xl shadow-2xl overflow-hidden z-50"
          data-testid="context-switcher-menu"
        >
          <div className="px-4 py-2 text-[9px] tracking-[0.25em] uppercase text-white/45 border-b border-white/10">
            Switch to
          </div>
          {contexts.map((c) => {
            const Icon = ICONS[c.icon] || User;
            const isActive = c.id === activeContext.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => { setActive(c); setOpen(false); }}
                className={
                  "w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.06] transition text-left "
                  + (isActive ? "bg-white/[0.04]" : "")
                }
                data-testid={`context-switcher-item-${c.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                  <Icon size={15} className="text-white/85" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/50">
                    {c.kind === "personal" ? "Personal profile" : "Page · " + c.kind.replace(/_/g, " ")}
                  </div>
                  <div className="text-sm font-display font-semibold truncate">{c.title}</div>
                </div>
                {isActive && <Check size={14} className="text-emerald-400 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
